// src/hooks/useAuthState.ts
import { useCallback, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { BB_AUTH_COMPLETE_MESSAGE } from "@/lib/auth/beginGoogleSignIn";
import { consumeAuthReturnInBrowser } from "@/lib/auth/consumeAuthReturnInBrowser";
import { ensureDevAutoLogin } from "@/lib/auth/devAutoLogin";
import {
  type AccessStatus,
  type AuthProfile,
  type AuthState,
  type UseAuthStateResult,
  authMetadataString,
  defaultAuthState,
  firstNonEmptyString,
  loadAccessibleLocationIds,
  loadActiveOrgCapabilityFlags,
  normalizeAccessStatus,
  syncLinkedExternalContacts,
} from "./authStateModel";
import { deriveAuthRolesFromUserRoles, type UserRolesRow } from "./authStateRoles";

export type {
  AccessStatus,
  AuthProfile,
  OrgRole,
  ActiveMembership,
  PlatformRole,
  AuthState,
  UseAuthStateResult,
} from "./authStateModel";
export { normalizeAccessStatus } from "./authStateModel";

export function useAuthState(): UseAuthStateResult {
  const [state, setState] = useState<AuthState>(() => {
    if (!supabase) {
      return { status: "no-supabase", session: null, profile: null, accessStatus: "unknown", ...defaultAuthState };
    }
    return { status: "idle", session: null, profile: null, accessStatus: "unknown", ...defaultAuthState };
  });
  const [refreshNonce, setRefreshNonce] = useState(0);
  const refreshProfile = useCallback(() => {
    setRefreshNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!supabase) return;

    let cancelled = false;

    const loadProfileForSession = async (session: Session | null) => {
      if (!session) {
        if (!cancelled) {
          setState({ status: "ready", session: null, profile: null, accessStatus: "unknown", ...defaultAuthState });
        }
        return;
      }

      setState((prev) => ({
        status: "loading",
        session,
        profile: prev.profile,
        accessStatus: prev.accessStatus ?? "unknown",
        activeMembership: prev.activeMembership ?? null,
        role: prev.role ?? "unassigned",
        platformRole: prev.platformRole ?? null,
        allRoles: prev.allRoles ?? [],
        accessibleLocationIds: prev.accessibleLocationIds ?? [],
        memberOrganizationIds: prev.memberOrganizationIds ?? [],
        orgRoles: prev.orgRoles ?? [],
        platformRoles: prev.platformRoles ?? [],
        hasSalesFloor: prev.hasSalesFloor ?? false,
        servicesEnabled: prev.servicesEnabled ?? false,
        personalAssistantEnabled: prev.personalAssistantEnabled ?? false,
      }));

      const client = supabase;
      if (!client) return;

      await syncLinkedExternalContacts(session);

      const primaryProfileSelect =
        "id, email, full_name, display_name, avatar_url, personal_phone, work_phone, email_domain, access_status, primary_organization_id, active_location_id";
      const fallbackProfileSelect =
        "id, email, full_name, display_name, avatar_url, email_domain, access_status, primary_organization_id, active_location_id";

      let { data: profileData, error: profileError } = await client
        .from("profiles")
        .select(primaryProfileSelect)
        .eq("id", session.user.id)
        .maybeSingle();

      if (profileError && /personal_phone|work_phone/i.test(profileError.message || "")) {
        const fallback = await client
          .from("profiles")
          .select(fallbackProfileSelect)
          .eq("id", session.user.id)
          .maybeSingle();

        profileData = fallback.data
          ? {
              ...fallback.data,
              personal_phone: null,
              work_phone: null,
            }
          : null;
        profileError = fallback.error;
      }

      if (cancelled) return;

      if (profileError) {
        console.error("[auth] failed to load profile", profileError);
        setState({
          status: "error",
          session,
          profile: null,
          accessStatus: "unknown",
          ...defaultAuthState,
          error: profileError.message,
        });
        return;
      }

      const rawProfile = (profileData as AuthProfile | null) ?? null;
      const emailLocalPart = session.user.email?.split("@")[0]?.trim() || null;
      const metadataFullName = firstNonEmptyString(
        authMetadataString(session, "full_name"),
        authMetadataString(session, "name"),
        authMetadataString(session, "display_name")
      );
      const metadataDisplayName = firstNonEmptyString(
        authMetadataString(session, "display_name"),
        authMetadataString(session, "name"),
        authMetadataString(session, "full_name")
      );
      const metadataAvatarUrl = firstNonEmptyString(
        authMetadataString(session, "avatar_url"),
        authMetadataString(session, "picture")
      );

      const normalizedProfile = rawProfile
        ? {
            ...rawProfile,
            access_status: normalizeAccessStatus(rawProfile.access_status),
            email: firstNonEmptyString(rawProfile.email, session.user.email) ?? null,
            full_name: firstNonEmptyString(rawProfile.full_name, metadataFullName),
            display_name:
              firstNonEmptyString(
                rawProfile.full_name,
                rawProfile.display_name && rawProfile.display_name !== emailLocalPart ? rawProfile.display_name : null,
                metadataFullName,
                metadataDisplayName
              ) ?? null,
            avatar_url: firstNonEmptyString(rawProfile.avatar_url, metadataAvatarUrl),
          }
        : null;

      const profile = normalizedProfile as AuthProfile | null;
      const accessStatus: AccessStatus = normalizeAccessStatus(profile?.access_status);

      let derived = deriveAuthRolesFromUserRoles({ rows: [], profile, accessStatus });

      if (profile) {
        const { data: userRolesData, error: rolesError } = await client
          .from("user_roles")
          .select("role, org_id")
          .eq("user_id", session.user.id)
          .eq("is_active", true);

        if (!cancelled && !rolesError && userRolesData) {
          const rows = userRolesData as UserRolesRow[];
          derived = deriveAuthRolesFromUserRoles({ rows, profile, accessStatus });
          if (derived.warnings.length > 0) {
            console.warn(
              "[auth] platform_* roles misfiled with org_id (expected org_id=null); honoring at runtime — clean up in user_roles:",
              derived.warnings
            );
          }
        } else if (!cancelled && accessStatus === "approved") {
          // Roles query failed (or returned no rows) but the user is approved:
          // preserve the legacy fallback of treating them as a basic sales user.
          derived = { ...derived, role: "sales" };
        }
      }

      // Active-org id resolution mirrors AuthContext.deriveValue:
      // organization-scoped membership wins, falling back to the
      // identity-level primary_organization_id for users without an
      // active membership row yet.
      const activeOrgIdForCapabilities =
        derived.activeMembership?.organization_id ?? profile?.primary_organization_id ?? null;

      // Fire location-id + org capability flags in parallel (fail closed).
      let accessibleLocationIds: string[] = [];
      let hasSalesFloor = false;
      let servicesEnabled = false;
      let personalAssistantEnabled = false;
      if (profile) {
        const [locResult, capResult] = await Promise.allSettled([
          loadAccessibleLocationIds(client, session.user.id),
          loadActiveOrgCapabilityFlags(client, activeOrgIdForCapabilities),
        ]);
        if (!cancelled && locResult.status === "fulfilled") {
          accessibleLocationIds = locResult.value;
        } else if (!cancelled && locResult.status === "rejected") {
          console.warn("[auth] failed to load accessible locations", locResult.reason);
        }
        if (!cancelled && capResult.status === "fulfilled") {
          hasSalesFloor = capResult.value.hasSalesFloor;
          servicesEnabled = capResult.value.servicesEnabled;
          personalAssistantEnabled = capResult.value.personalAssistantEnabled;
        }
      }

      if (cancelled) return;

      setState({
        status: "ready",
        session,
        profile,
        accessStatus,
        activeMembership: derived.activeMembership,
        role: derived.role,
        platformRole: derived.platformRole,
        allRoles: derived.allRoles,
        accessibleLocationIds,
        memberOrganizationIds: derived.memberOrganizationIds,
        orgRoles: derived.orgRoles,
        platformRoles: derived.platformRoles,
        hasSalesFloor,
        servicesEnabled,
        personalAssistantEnabled,
      });
    };

    const client = supabase;
    if (!client) return;

    void (async () => {
      await consumeAuthReturnInBrowser(client);
      if (cancelled) return;
      await ensureDevAutoLogin(client);
      if (cancelled) return;
      const { data, error } = await client.auth.getSession();
      if (cancelled) return;
      if (error) {
        console.error("[auth] getSession error", error);
      }
      void loadProfileForSession(data.session ?? null);
    })();

    const { data } = client.auth.onAuthStateChange((event, session) => {
      if (event === "TOKEN_REFRESHED" || event === "SIGNED_IN") {
        let isNewUser = false;
        setState((prev) => {
          const alreadyLoaded =
            (prev.status === "ready" || prev.status === "loading") &&
            prev.session?.user.id != null &&
            prev.session.user.id === session?.user.id;
          if (alreadyLoaded) return { ...prev, session };
          isNewUser = true;
          return prev;
        });
        if (isNewUser) void loadProfileForSession(session);
        return;
      }
      void loadProfileForSession(session);
    });

    const onAuthPopup = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if ((event.data as { type?: string } | null)?.type !== BB_AUTH_COMPLETE_MESSAGE) return;
      void (async () => {
        const { data: next } = await client.auth.getSession();
        if (!cancelled) void loadProfileForSession(next.session ?? null);
      })();
    };
    window.addEventListener("message", onAuthPopup);

    return () => {
      cancelled = true;
      data.subscription.unsubscribe();
      window.removeEventListener("message", onAuthPopup);
    };
  }, [refreshNonce]);

  return {
    ...state,
    refreshProfile,
  };
}
